import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface KineticInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const KineticInsights: React.FC<KineticInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Instant snap scale transition
  const opacity = interpolate(elapsedFrames, [0, 4], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const scale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.85,
    to: 1,
    config: { damping: 11, stiffness: 140, mass: 0.4 }, // highly kinetic snappy bounce!
  });

  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardTranslateY = spring({
    frame: cardElapsed,
    fps,
    from: 60,
    to: 0,
    config: { damping: 12, mass: 0.5, stiffness: 110 },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background:
            "linear-gradient(135deg, rgba(20, 10, 30, 0.9) 0%, rgba(5, 5, 10, 0.95) 100%)",
          border: "4px solid #ffd166", // bold bright border
          borderRadius: "0px", // sharp block edges
          boxShadow: "10px 10px 0px rgba(255, 209, 102, 0.3)", // solid offset shadow!
          padding: "40px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          position: "relative",
          opacity: cardOpacity,
          transform: `translateY(${cardTranslateY}px)`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              fontFamily: "'Anton', sans-serif",
              fontWeight: 900,
              fontSize: "22px",
              color: "#ffd166",
              letterSpacing: "3px",
              textTransform: "uppercase",
              transform: "skewX(-10deg)",
            }}
          >
            🔥 TAKEAWAY #{activeIndex + 1}
          </div>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {bullets.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === activeIndex ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  background:
                    i === activeIndex ? "#ffd166" : "rgba(255,209,102,0.25)",
                  boxShadow:
                    i === activeIndex
                      ? "0 0 8px rgba(255,209,102,0.5)"
                      : "none",
                }}
              />
            ))}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#ffd166"
                accentColor="#ffd166"
                size={68}
                style="badge"
                opacity={opacity}
              />
            ) : null;
          })()}
        </div>

        <div
          style={{
            opacity,
            transform: `scale(${scale})`,
            minHeight: "290px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Anton', sans-serif",
              fontSize: "58px", // bold compressed font fits extremely well at 58px!
              lineHeight: 1.2,
              color: "#ffffff",
              textAlign: "left",
              textTransform: "uppercase",
              letterSpacing: "1px",
              transform: "skewX(-4deg)",
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 900,
                fontStyle: "italic",
                color: "#ff007f",
                background: "#ffd166",
                padding: "0 10px",
                display: "inline-block",
                transform: "skew(-15deg)",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
